import axios from "axios";

/** Relative API path for Conectia's monthly validation (super admins only). */
export const CONECTIA_VALIDATION = "affiliate-validation/conectia";

/** `code` on the error thrown when `REACT_APP_SITE_URL` is not set. */
export const SITE_URL_MISSING = "SITE_URL_MISSING";

/**
 * Absolute URL of one Conectia validation endpoint, built like the other
 * crud files (`REACT_APP_SITE_URL` + path). Throws before any request is made
 * when `REACT_APP_SITE_URL` is not set, rather than sending a relative URL to
 * the admin site's own host.
 * @param {string} action - `check`, `save` or `download`.
 * @returns {string} The endpoint URL.
 * @throws {Error} With `code` SITE_URL_MISSING when `REACT_APP_SITE_URL` is missing or empty.
 */
function conectiaValidationUrl(action) {
  const site = import.meta.env.REACT_APP_SITE_URL;
  if (!site) {
    const error = new Error("REACT_APP_SITE_URL is not set, so the Conectia validation API cannot be reached.");
    error.code = SITE_URL_MISSING;
    throw error;
  }
  return `${site}${CONECTIA_VALIDATION}/${action}`;
}

/**
 * Form data carrying Conectia's file, the activity month and any statuses
 * chosen by hand.
 * @param {File} file - Conectia's validation file (CSV or XLSX).
 * @param {string} [activityMonth] - `YYYY-MM` of the activity the report covers.
 * @param {Array<{line: number, status: string, reason: string}>} [overrides=[]] - Lines whose status was changed, keyed by file line.
 * @returns {FormData} The multipart body.
 */
function fileForm(file, activityMonth, overrides = []) {
  const form = new FormData();
  form.append("file", file);
  if (activityMonth) {
    form.append("activity_month", activityMonth);
  }
  overrides.forEach((override, index) => {
    form.append(`overrides[${index}][line]`, String(override.line));
    form.append(`overrides[${index}][status]`, override.status);
    form.append(`overrides[${index}][reason]`, override.reason || "");
  });
  return form;
}

/**
 * Uploads Conectia's file and gets every line back with a suggested status.
 * @param {File} file - Conectia's validation file.
 * @param {string} [activityMonth] - `YYYY-MM`; the server defaults to last month.
 * @returns {import("axios").AxiosPromise} Axios POST resolving to the checked lines.
 */
export function checkConectiaFile(file, activityMonth) {
  return axios.post(conectiaValidationUrl("check"), fileForm(file, activityMonth));
}

/**
 * Saves the decided statuses for matched bookings.
 * @param {Array<{order_id: number, status: string, reason: string}>} lines - One entry per matched booking.
 * @returns {import("axios").AxiosPromise} Axios POST resolving to the saved counts.
 */
export function saveConectiaDecisions(lines) {
  return axios.post(conectiaValidationUrl("save"), { lines });
}

/**
 * Builds the reply file for Conectia (CSV) from the same file and the
 * statuses chosen by hand.
 * @param {File} file - Conectia's validation file, as checked.
 * @param {string} [activityMonth] - `YYYY-MM`.
 * @param {Array<{line: number, status: string, reason: string}>} overrides - Lines whose status was changed.
 * @returns {import("axios").AxiosPromise} Axios POST resolving to a blob.
 */
export function downloadConectiaReply(file, activityMonth, overrides) {
  return axios.post(
    conectiaValidationUrl("download"),
    fileForm(file, activityMonth, overrides),
    { responseType: "blob" },
  );
}
